import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { api, ApiError } from '../api';
import { leaveGuarded } from '../ui/unsaved';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { formatPln } from '../catalog/money';
import { RowMenu } from '../ui/RowMenu';
import { BankAccountsSettings } from './BankAccountsSettings';
import { dayLabel, ibanLabel } from './format';
import { TransactionsTable } from './TransactionsTable';
import { BANK_KEY, BANK_RESOURCE, type BankAccount, type InboxStatus, type Statement, type UploadResult } from './types';
import { UploadResults, UploadStatements } from './UploadStatements';
import '../finance/finance.css';
import './bank.css';

const SOURCES: Record<Statement['source'], string> = { upload: 'wgrany', folder: 'z folderu', auto: 'automatycznie' };
const FORMATS: Record<Statement['format'], string> = { camt053: 'XML camt.053', mt940: 'MT940' };
const timeLabel = (iso: string) => new Date(iso).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw', day: 'numeric', month: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });

/** "Stan folderu": when the server folder was last checked, how many files wait, the last problem. */
function FolderStatus() {
  const status = useQuery({ queryKey: [...BANK_KEY, 'inbox'], queryFn: () => api<InboxStatus>('/api/bank/inbox-status'), refetchInterval: 60_000 });
  const s = status.data;
  if (!s) return null;
  return (
    <p className="muted bank-folder" data-feature-id="bank.folder">
      Folder na serwerze: {s.lastRunAt ? `sprawdzony ${timeLabel(s.lastRunAt)}` : 'jeszcze nie sprawdzany'}
      {s.waiting > 0 && ` · czeka plików: ${s.waiting}`}
      {s.lastError && (
        <>
          {' · '}
          <span className="bank-folder-error">ostatni błąd: {s.lastError}</span>
        </>
      )}
    </p>
  );
}

function StatementsTable({ rows }: { rows: Statement[] }) {
  const showBalances = rows.some((r) => r.closingGrosze !== undefined);
  return (
    <div className="table-wrap">
      <table className="table bank-table" aria-label="Wczytane wyciągi">
        <thead>
          <tr>
            <th scope="col">Okres</th>
            <th scope="col" className="wide-only">
              Rachunek
            </th>
            <th scope="col" className="num">
              Operacje <span className="wide-only">(nowe / wszystkie)</span>
            </th>
            {showBalances && (
              <>
                <th scope="col" className="num wide-only">
                  Saldo początkowe
                </th>
                <th scope="col" className="num">
                  Saldo końcowe
                </th>
              </>
            )}
            <th scope="col" className="wide-only">
              Plik
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id}>
              <td className="nowrap">
                {dayLabel(s.periodFrom)} – {dayLabel(s.periodTo)}
              </td>
              <td className="nowrap wide-only">{ibanLabel(s.iban).slice(-9)}</td>
              <td className="num">
                {s.newEntries} / {s.entries}
              </td>
              {showBalances && (
                <>
                  <td className="num nowrap wide-only">{s.openingGrosze === undefined ? '—' : formatPln(s.openingGrosze)}</td>
                  <td className="num nowrap">{s.closingGrosze === undefined ? '—' : formatPln(s.closingGrosze)}</td>
                </>
              )}
              <td className="wide-only">
                {s.fileId ? (
                  <a href={`/api/files/${s.fileId}`} target="_blank" rel="noreferrer">
                    {s.fileName}
                  </a>
                ) : (
                  s.fileName
                )}
                <div className="muted small">
                  {FORMATS[s.format]}, {SOURCES[s.source]} {timeLabel(s.importedAt)}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * "Finanse → Wyciągi" (F37, spec E3 §7): "Wgraj wyciąg", the statements read (period, new/all operations, balances),
 * the server folder's state and the operations. The company's accounts behind the ⚙.
 */
export function StatementsPage() {
  const { me } = useMe();
  const [upload, setUpload] = useState<{ results: UploadResult[]; error: string }>({ results: [], error: '' });
  const [showAccounts, setShowAccounts] = useState(false);
  const statements = useQuery({ queryKey: [...BANK_KEY, 'statements'], queryFn: () => api<{ statements: Statement[] }>('/api/bank/statements') });
  const accounts = useQuery({ queryKey: [...BANK_KEY, 'accounts'], queryFn: () => api<{ accounts: BankAccount[] }>('/api/bank/accounts') });
  if (!me) return null;
  const rows = statements.data?.statements ?? [];
  const noAccount = accounts.data !== undefined && accounts.data.accounts.length === 0;
  const mayEditAccounts = can(me, BANK_RESOURCE, 'edit');
  return (
    <section className="page wide bank-page" data-feature-id="bank.statements">
      <div className="bank-head">
        <p className="lede">Wyciągi z ING Business (XML camt.053 albo MT940). Każdy plik i każda operacja są wczytywane tylko raz.</p>
        <div className="actions row-actions">
          {can(me, BANK_RESOURCE, 'create') && <UploadStatements onResult={setUpload} />}
          <RowMenu icon="gear" label="Ustawienia wyciągów" featureId="bank.accounts" actions={[{ label: showAccounts ? 'Ukryj rachunki' : 'Rachunki', onSelect: () => (showAccounts ? leaveGuarded(() => setShowAccounts(false)) : setShowAccounts(true)) }]} />
        </div>
      </div>
      {noAccount && !showAccounts && (
        <p className="fin-warn">
          Najpierw dodaj rachunek firmy — wyciągi innych rachunków są odrzucane.{' '}
          <button className="btn small" type="button" onClick={() => setShowAccounts(true)}>
            Rachunki
          </button>
        </p>
      )}
      {showAccounts && <BankAccountsSettings editable={mayEditAccounts} onClose={() => setShowAccounts(false)} />}
      <UploadResults {...upload} />
      <FolderStatus />

      <h2>Wczytane wyciągi</h2>
      {statements.error && (
        <p className="alert" role="alert">
          {statements.error instanceof ApiError ? statements.error.message : 'Nie udało się wczytać wyciągów'}
        </p>
      )}
      {statements.data && rows.length === 0 && <p className="muted">Nie wczytano jeszcze żadnego wyciągu.</p>}
      {rows.length > 0 && <StatementsTable rows={rows} />}

      <TransactionsTable />
    </section>
  );
}
